import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Trophy, Target, Flame, Star, Gift, Users, Zap } from 'lucide-react';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const Achievements = () => {
  const [achievements, setAchievements] = useState([]);
  const [userAchievements, setUserAchievements] = useState([]);
  const [stats, setStats] = useState({ totalPoints: 0, streak: 0, level: 1 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [achievementsRes, userStatsRes] = await Promise.all([
        api.get('/achievements'),
        api.get('/user/stats')
      ]);
      setAchievements(achievementsRes.data.achievements || []);
      setUserAchievements(achievementsRes.data.user_achievements || []);
      setStats(userStatsRes.data || { totalPoints: 0, streak: 0, level: 1 });
    } catch (error) {
      console.error('Error fetching achievements:', error);
    }
    setLoading(false);
  };

  const isUnlocked = (achievementId) => {
    return userAchievements.some(ua => ua.achievement_id === achievementId);
  };

  const categoryIcons = {
    beginner: Target,
    streak: Flame,
    meals: Star,
    recipes: Gift,
    scanner: Zap,
    health: Trophy,
    habits: Star,
    social: Users,
    referral: Gift,
    challenges: Trophy,
    features: Zap
  };

  const levelThresholds = [0, 100, 250, 500, 1000, 2000, 5000];
  const currentLevel = levelThresholds.findIndex(t => stats.totalPoints < t) || levelThresholds.length;
  const nextLevelPoints = levelThresholds[currentLevel] || levelThresholds[levelThresholds.length - 1];
  const prevLevelPoints = levelThresholds[currentLevel - 1] || 0;
  const levelProgress = ((stats.totalPoints - prevLevelPoints) / (nextLevelPoints - prevLevelPoints)) * 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-gradient-to-br from-yellow-400 to-orange-500 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-100">Level</p>
                <p className="text-4xl font-bold">{currentLevel}</p>
              </div>
              <Trophy className="w-12 h-12 opacity-80" />
            </div>
            <div className="mt-4">
              <Progress value={levelProgress} className="h-2 bg-yellow-300" />
              <p className="text-xs mt-1 text-yellow-100">{stats.totalPoints} / {nextLevelPoints} XP</p>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-400 to-pink-500 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-100">Current Streak</p>
                <p className="text-4xl font-bold">{stats.streak} days</p>
              </div>
              <Flame className="w-12 h-12 opacity-80" />
            </div>
            <p className="text-sm mt-2 text-red-100">Keep logging daily!</p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-400 to-teal-500 text-white">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100">Badges Earned</p>
                <p className="text-4xl font-bold">{userAchievements.length}/{achievements.length}</p>
              </div>
              <Star className="w-12 h-12 opacity-80" />
            </div>
            <p className="text-sm mt-2 text-emerald-100">Total: {stats.totalPoints} points</p>
          </CardContent>
        </Card>
      </div>

      {/* Achievements Grid */}
      <Card className="dark:bg-gray-800">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 dark:text-white">
            <Trophy className="w-6 h-6 text-yellow-500" />
            Achievements & Badges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {achievements.map((achievement) => {
              const unlocked = isUnlocked(achievement.id);
              const Icon = categoryIcons[achievement.category] || Trophy;
              
              return (
                <div
                  key={achievement.id}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    unlocked
                      ? 'bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-400 dark:from-yellow-900/20 dark:to-orange-900/20'
                      : 'bg-gray-50 border-gray-200 opacity-60 dark:bg-gray-700 dark:border-gray-600'
                  }`}
                >
                  <div className="text-center">
                    <div className={`text-4xl mb-2 ${unlocked ? '' : 'grayscale'}`}>
                      {achievement.icon}
                    </div>
                    <h3 className={`font-semibold text-sm ${
                      unlocked ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {achievement.name}
                    </h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {achievement.description}
                    </p>
                    <Badge className={`mt-2 ${
                      unlocked
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}>
                      +{achievement.points} XP
                    </Badge>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Achievements;
